/**
 * Clientes do Gerenciador de Produção, agrupados a partir das linhas da
 * planilha (uma linha = um ambiente). Funções puras: recebem as linhas que o
 * DadosContext já leu e devolvem cliente → pedidos → ambientes, com saúde e
 * atraso calculados. Nada aqui consulta o backend.
 */
import { formatarData, parseNumero } from "../../../../utils/calculos";
import {
  CLIENTE_STATUS_FLOW,
  getAmbienteCurrentStatus,
  getEntregaDeadline,
  getPedidoIdentity,
  isMeaningfulIdentifier,
} from "../../../../utils/clientes";
import { hasValue, normalizeText, toDisplayText } from "../../../../utils/data";
import { F } from "../../../../utils/gerencialFields";
import { compareDatesAsc, startOfDay } from "../../../../utils/status";

export const SEM_VALOR = "—";
const MS_POR_DIA = 1000 * 60 * 60 * 24;
/** Orçamento de atraso por ambiente que zera a saúde (30 dias). */
const ATRASO_MAXIMO_POR_AMBIENTE = 30;
const STATUS_ENTREGUE = "Entregue";
const INDICE_DO_STATUS = new Map(CLIENTE_STATUS_FLOW.map((status, indice) => [status, indice]));
const PESO_DO_TOM = {
  default: 0,
  neutral: 0,
  info: 0,
  purple: 0,
  lime: 0,
  orange: 0,
  success: 1,
  warning: 2,
  today: 3,
  danger: 4,
};

export const SITUACOES = Object.freeze({
  TODOS: "todos",
  PENDENTES: "pendentes",
  EM_ANDAMENTO: "andamento",
  CONCLUIDOS: "concluidos",
});

export function compararTexto(a, b) {
  return String(a ?? "").localeCompare(String(b ?? ""), "pt-BR", { numeric: true, sensitivity: "base" });
}

function valoresUnicos(linhas, campo, aceita = hasValue) {
  return [...new Set(linhas.map((linha) => linha[campo]).filter(aceita).map((v) => String(v).trim()))];
}

function datasOrdenadas(valores) {
  return valores.filter(hasValue).sort((a, b) => compareDatesAsc(a, b));
}

function primeiraData(valores) {
  return datasOrdenadas(valores)[0] ?? null;
}

function ultimaData(valores) {
  const datas = datasOrdenadas(valores);
  return datas.length ? datas[datas.length - 1] : null;
}

function faixaDeDatas(valores) {
  const inicio = primeiraData(valores);
  const fim = ultimaData(valores);
  if (!inicio && !fim) return SEM_VALOR;
  if (!inicio || !fim || compareDatesAsc(inicio, fim) === 0) return formatarData(inicio || fim);
  return `${formatarData(inicio)} a ${formatarData(fim)}`;
}

function identificador(valor, reserva = SEM_VALOR) {
  return isMeaningfulIdentifier(valor) ? String(valor).trim() : reserva;
}

/** Id da rota /legado/clientes/:id — o nome do cliente sem acento, em kebab-case. */
export function idDaRota(valor) {
  return (
    normalizeText(valor)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "cliente"
  );
}

function diasPositivosEntre(inicio, fim) {
  const de = startOfDay(inicio);
  const ate = startOfDay(fim);
  if (!de || !ate || ate <= de) return 0;
  return Math.round((ate.getTime() - de.getTime()) / MS_POR_DIA);
}

/**
 * Data real da entrega: CN (CARREGAMENTO), digitada à mão. CO (ENTREGA
 * CLIENTE) é fórmula de previsão e não conta como entrega feita.
 */
export function dataDeEntregaReal(linha) {
  return linha[F.CARREGAMENTO] || null;
}

function diasDeAtrasoNoContrato(linha, hoje) {
  return diasPositivosEntre(linha[F.ENTREGA_PREVISTA], dataDeEntregaReal(linha) || hoje);
}

function contarStatus(ambientes) {
  const contagem = new Map();
  function contar(status, tom) {
    const atual = contagem.get(status) ?? { label: status, count: 0, tone: tom };
    atual.count += 1;
    if ((PESO_DO_TOM[tom] ?? 0) > (PESO_DO_TOM[atual.tone] ?? 0)) atual.tone = tom;
    contagem.set(status, atual);
  }
  ambientes.forEach((ambiente) => {
    contar(ambiente.status, ambiente.statusVariant);
    // Entregue com terceiros/acabamentos abertos conta também na pendência:
    // o chip "Aguardando Fornecedores Terceiros" precisa achar esse ambiente.
    (ambiente.pendencias || []).forEach((p) => contar(p.status, p.statusVariant));
  });
  return CLIENTE_STATUS_FLOW.map((status) => contagem.get(status)).filter(Boolean);
}

/**
 * Saúde de 0 a 100: o atraso acumulado contra o contrato, em proporção a um
 * teto de 30 dias por ambiente com data de contrato.
 */
export function saudeDosAmbientes(ambientes) {
  const comContrato = ambientes.filter((a) => hasValue(a.entregaContrato));
  const atrasoTotal = comContrato.reduce((soma, a) => soma + a.contractDelayDays, 0);
  const teto = comContrato.length * ATRASO_MAXIMO_POR_AMBIENTE;
  const saude = teto === 0 ? 100 : Math.max(0, Math.round(100 - (atrasoTotal / teto) * 100));
  return {
    delayedAmbientesCount: comContrato.filter((a) => a.contractDelayDays > 0).length,
    totalDelayDays: atrasoTotal,
    ambientesComContratoCount: comContrato.length,
    healthPercentage: saude,
  };
}

export function situacaoDoCliente(cliente) {
  if (cliente.totalAmbientes === 0) return SITUACOES.TODOS;
  if (cliente.totalEntregues === cliente.totalAmbientes) return SITUACOES.CONCLUIDOS;
  if (cliente.totalAmbientesAguardandoLiberacao === cliente.totalAmbientes) return SITUACOES.PENDENTES;
  return SITUACOES.EM_ANDAMENTO;
}

function montarAmbiente(linha, indice, pedidoId, hoje) {
  const status = getAmbienteCurrentStatus(linha, { referenceDate: hoje });
  return {
    id: `${pedidoId}-${indice}`,
    ambiente: toDisplayText(linha[F.AMBIENTE], "Ambiente não informado"),
    lote: identificador(linha[F.NUMERO_DO_LOTE]),
    vendedor: toDisplayText(linha[F.VENDEDOR], SEM_VALOR),
    consultor: toDisplayText(linha[F.CONSULTOR], SEM_VALOR),
    revisao: toDisplayText(linha[F.REVISAO], SEM_VALOR),
    prazo: toDisplayText(linha[F.PRAZO_EM_DIAS], SEM_VALOR),
    marceneiro: toDisplayText(linha[F.MARCENEIRO_RESPONSAVEL], SEM_VALOR),
    dataVenda: linha[F.DATA_DA_VENDA] || null,
    entregaContrato: linha[F.ENTREGA_PREVISTA],
    dataReprogramada: linha[F.DATA_REPROGRAMADA],
    valor: linha[F.VALOR_DO_AMBIENTE],
    status: status.status,
    statusVariant: status.statusVariant,
    pendencias: status.pendencias,
    statusDeadline: status.deadline,
    actualDeliveryDate: dataDeEntregaReal(linha),
    contractDelayDays: diasDeAtrasoNoContrato(linha, hoje),
    row: linha,
  };
}

function ordenarAmbientes(a, b) {
  const porStatus =
    (INDICE_DO_STATUS.get(a.status) ?? Number.MAX_SAFE_INTEGER) -
    (INDICE_DO_STATUS.get(b.status) ?? Number.MAX_SAFE_INTEGER);
  if (porStatus !== 0) return porStatus;
  const porPrazo = compareDatesAsc(a.statusDeadline, b.statusDeadline);
  if (porPrazo !== 0) return porPrazo;
  return compararTexto(a.ambiente, b.ambiente);
}

function proximaEntregaDas(linhas) {
  return primeiraData(linhas.filter((l) => !hasValue(dataDeEntregaReal(l))).map((l) => getEntregaDeadline(l)));
}

function somaDosValores(linhas) {
  return linhas.reduce((soma, linha) => soma + (parseNumero(linha[F.VALOR_DO_AMBIENTE]) || 0), 0);
}

function montarPedido(pedido, hoje) {
  const ambientes = pedido.rows.map((linha, i) => montarAmbiente(linha, i, pedido.id, hoje)).sort(ordenarAmbientes);
  const totalEntregues = ambientes.filter((a) => a.status === STATUS_ENTREGUE).length;
  return {
    id: pedido.id,
    label: pedido.label,
    numero: identificador(pedido.rows[0]?.[F.NUMERO_DO_PEDIDO], ""),
    lotes: valoresUnicos(pedido.rows, F.NUMERO_DO_LOTE, isMeaningfulIdentifier),
    vendedores: valoresUnicos(pedido.rows, F.VENDEDOR),
    consultores: valoresUnicos(pedido.rows, F.CONSULTOR),
    revisoes: valoresUnicos(pedido.rows, F.REVISAO),
    prazos: valoresUnicos(pedido.rows, F.PRAZO_EM_DIAS),
    dataVenda: primeiraData(pedido.rows.map((l) => l[F.DATA_DA_VENDA])),
    dataVendaLabel: faixaDeDatas(pedido.rows.map((l) => l[F.DATA_DA_VENDA])),
    entregaContratoLabel: faixaDeDatas(pedido.rows.map((l) => l[F.ENTREGA_PREVISTA])),
    reprogramacaoLabel: faixaDeDatas(pedido.rows.map((l) => l[F.DATA_REPROGRAMADA])),
    totalAmbientes: ambientes.length,
    totalEntregues,
    totalEmAndamento: ambientes.length - totalEntregues,
    valorTotal: somaDosValores(pedido.rows),
    statusCounts: contarStatus(ambientes),
    ambientes,
    delayedAmbientesCount: ambientes.filter((a) => a.contractDelayDays > 0).length,
    totalDelayDays: ambientes.reduce((soma, a) => soma + a.contractDelayDays, 0),
    proximaEntrega: proximaEntregaDas(pedido.rows),
  };
}

function observacoesDoCliente(clienteId, linhas) {
  return linhas
    .filter((linha) => hasValue(linha[F.OBSERVACOES_GERAIS]))
    .map((linha, i) => ({
      id: `${clienteId}-observacao-${i}`,
      pedido: identificador(linha[F.NUMERO_DO_PEDIDO], "Pedido não informado"),
      ambiente: toDisplayText(linha[F.AMBIENTE], "Ambiente não informado"),
      observacao: String(linha[F.OBSERVACOES_GERAIS]).trim(),
    }))
    .sort((a, b) => compararTexto(a.pedido, b.pedido) || compararTexto(a.ambiente, b.ambiente));
}

function agruparPorCliente(linhas) {
  const grupos = new Map();
  linhas.forEach((linha, indice) => {
    if (!hasValue(linha[F.NOME_DO_CLIENTE])) return;
    const nome = String(linha[F.NOME_DO_CLIENTE]).trim();
    const chave = normalizeText(nome);
    const pedido = getPedidoIdentity(linha, indice);
    if (!grupos.has(chave)) grupos.set(chave, { id: idDaRota(nome), cliente: nome, rows: [], pedidos: new Map() });
    const grupo = grupos.get(chave);
    grupo.rows.push(linha);
    if (!grupo.pedidos.has(pedido.key)) grupo.pedidos.set(pedido.key, { id: pedido.key, label: pedido.label, rows: [] });
    grupo.pedidos.get(pedido.key).rows.push(linha);
  });
  return [...grupos.values()];
}

function montarCliente(grupo, hoje) {
  const pedidos = [...grupo.pedidos.values()]
    .map((pedido) => montarPedido(pedido, hoje))
    .sort((a, b) => compareDatesAsc(a.proximaEntrega, b.proximaEntrega) || compararTexto(a.label, b.label));
  const ambientes = pedidos.flatMap((p) => p.ambientes);
  const totalAmbientes = ambientes.length;
  const totalEntregues = pedidos.reduce((soma, p) => soma + p.totalEntregues, 0);
  const linhas = grupo.rows;
  return {
    id: grupo.id,
    cliente: grupo.cliente,
    totalPedidos: pedidos.length,
    totalAmbientes,
    totalEntregues,
    totalEmAndamento: totalAmbientes - totalEntregues,
    totalAmbientesAguardandoLiberacao: ambientes.filter((a) => a.status === CLIENTE_STATUS_FLOW[0]).length,
    valor: somaDosValores(linhas),
    valorEmCarteira: somaDosValores(linhas.filter((l) => !hasValue(dataDeEntregaReal(l)))),
    vendedores: valoresUnicos(linhas, F.VENDEDOR),
    consultores: valoresUnicos(linhas, F.CONSULTOR),
    marceneiros: valoresUnicos(linhas, F.MARCENEIRO_RESPONSAVEL),
    lotes: valoresUnicos(linhas, F.NUMERO_DO_LOTE, isMeaningfulIdentifier),
    revisoes: valoresUnicos(linhas, F.REVISAO),
    prazos: valoresUnicos(linhas, F.PRAZO_EM_DIAS),
    numerosDosPedidos: valoresUnicos(linhas, F.NUMERO_DO_PEDIDO, isMeaningfulIdentifier).sort(compararTexto),
    primeiraVenda: primeiraData(linhas.map((l) => l[F.DATA_DA_VENDA])),
    proximaEntrega: proximaEntregaDas(linhas),
    ultimaEntrega: ultimaData(linhas.map((l) => dataDeEntregaReal(l))),
    observacoesDetalhadas: observacoesDoCliente(grupo.id, linhas),
    obrasFuturas: linhas.filter((l) => normalizeText(l[F.OBRA_FUTURA]) === "SIM").length,
    pedidos,
    ...saudeDosAmbientes(ambientes),
  };
}

/** Linhas da planilha → clientes ordenados por nome. `hoje` fixa a data nos testes. */
export function montarClientes(linhas, hoje = new Date()) {
  return agruparPorCliente(linhas || [])
    .map((grupo) => montarCliente(grupo, hoje))
    .sort((a, b) => compararTexto(a.cliente, b.cliente));
}
